"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerViewHandlebarsHelpers = void 0;
const luxon_1 = require("luxon");
const country_1 = require("../internationalization/enums/country");
const dates_1 = require("../internationalization/features/dates");
const de_amount_in_words_1 = require("../internationalization/features/de-amount-in-words");
const receipt_1 = require("../internationalization/features/receipt");
const country_2 = require("./country");
const currency_1 = require("./currency");
const dates_2 = require("./dates");
// An e-ticket must read the same whichever worker renders it, so an event with no timezone falls back
// to UTC — what isDateSameDayDependingOnTimezone already assumes — instead of the server's local zone.
const eventZone = (eventTimezone) => eventTimezone || "UTC";
const isConditionMet = (v1, operator, v2) => {
    switch (operator) {
        case "==":
            // eslint-disable-next-line eqeqeq
            return v1 == v2;
        case "===":
            return v1 === v2;
        case "!=":
            // eslint-disable-next-line eqeqeq
            return v1 != v2;
        case "!==":
            return v1 !== v2;
        case "<":
            return v1 < v2;
        case "<=":
            return v1 <= v2;
        case ">":
            return v1 > v2;
        case ">=":
            return v1 >= v2;
        case "&&":
            return Boolean(v1 && v2);
        case "||":
            return Boolean(v1 || v2);
        default:
            return false;
    }
};
// One shared object for every block: nested ifCond blocks then keep `../` pointing at the template root.
const IF_COND_BLOCK_CONTEXT = Object.freeze({});
// Shared by the server PDF views (receipts, tickets, coupons) and their Storybook previews.
const registerViewHandlebarsHelpers = (instance) => {
    instance.registerHelper("ifCond", (v1, operator, v2, options) => isConditionMet(v1, operator, v2) ? options.fn(IF_COND_BLOCK_CONTEXT) : options.inverse(IF_COND_BLOCK_CONTEXT));
    // nestjs-i18n's `t` helper ignores Handlebars hash arguments, so templates pass them as `(args name=value)`.
    instance.registerHelper("args", (options) => options.hash);
    instance.registerHelper("increment", (value) => {
        return parseInt(String(value)) + 1;
    });
    instance.registerHelper("toAmount", (amount, isoLocale, country) => {
        return (0, currency_1.formatCurrencyAmountByCountry)({ value: (0, currency_1.getDollarValue)(amount), locale: isoLocale, country });
    });
    // Same shape as the web ticket page header, e.g. "Sat, Sep 26, 2025"
    instance.registerHelper("toEventDate", (datetime, isoLocale, eventTimezone) => {
        return (0, dates_1.formatDateTimeToLocaleString)(datetime, isoLocale, { weekday: "short", month: "short", day: "numeric", year: "numeric" }, eventZone(eventTimezone));
    });
    instance.registerHelper("toTime", (datetime, isoLocale, eventTimezone) => {
        return (0, dates_1.formatDateTimeToLocaleString)(datetime, isoLocale, dates_2.StartTicketEndTimePreset, eventZone(eventTimezone));
    });
    instance.registerHelper("toTimeWithTz", (datetime, isoLocale, eventTimezone) => {
        // "10:00 AM PDT"
        return (0, dates_1.formatDateTimeToLocaleString)(datetime, isoLocale, dates_2.EndTicketEndTimePreset, eventZone(eventTimezone));
    });
    instance.registerHelper("toDate", (datetime, isoLocale) => {
        return (0, dates_2.formatDateToFullDate)(datetime, isoLocale);
    });
    instance.registerHelper("formatDate", (date, format) => {
        const parsedDate = luxon_1.DateTime.fromISO(date);
        return parsedDate.toFormat(format);
    });
    instance.registerHelper("formatDateToUserLocale", (date, locale) => {
        const parsedDate = luxon_1.DateTime.fromISO(date);
        const testFormat = locale === "FR" ? "d MMMM yyyy" : "MMMM d, yyyy";
        return parsedDate.toFormat(testFormat, { locale });
    });
    instance.registerHelper("toDateTime", (datetime, isoLocale) => {
        return (0, dates_2.formatDateToFullDateTime)(datetime, isoLocale);
    });
    instance.registerHelper("toDateTimeWithHourAndMinute", (datetime, isoLocale, eventTimezone) => {
        return (0, dates_1.formatDateTimeToLocaleString)(datetime, isoLocale, { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }, eventTimezone);
    });
    instance.registerHelper("slice", (string, number) => {
        return string.slice(0, number);
    });
    instance.registerHelper("toOrganizationCountry", (country) => {
        return country ? country_1.ORGANIZATION_COUNTRY_MAP[country].countryName : undefined;
    });
    instance.registerHelper("toCountry", (country) => {
        // Some receipts store a full country name instead of a code — display it as-is
        return (0, country_2.convertCountryCodeToCountryName)(country) ?? country;
    });
    instance.registerHelper("toUpper", (value) => {
        return typeof value === "string" ? value.toUpperCase() : value;
    });
    instance.registerHelper("toAustralianStateAbbreviation", (region) => {
        return (0, receipt_1.getAustralianStateAbbreviation)(region) ?? "";
    });
    instance.registerHelper("isAustralianCountry", (country) => {
        if (typeof country !== "string") {
            return false;
        }
        const normalized = country.trim().toUpperCase();
        return normalized === "AU" || normalized === "AUSTRALIA";
    });
    instance.registerHelper("isGermanCountry", (country) => {
        if (typeof country !== "string") {
            return false;
        }
        const normalized = country.trim().toUpperCase();
        return normalized === "DE" || normalized === "GERMANY" || normalized === "DEUTSCHLAND";
    });
    instance.registerHelper("toDeAmountInWords", (amountInCents) => {
        return (0, de_amount_in_words_1.formatDeAmountInWordsTamperProof)(amountInCents);
    });
    instance.registerHelper("formatValueToBulletListOrString", (value) => {
        if (!Array.isArray(value)) {
            return value;
        }
        const values = value;
        if (values.length > 1) {
            const stringArray = values.filter((item) => typeof item === "string" && item.trim() !== "");
            if (stringArray.length > 0) {
                return new instance.SafeString('<ul class="bullet-list">' +
                    stringArray.map((item) => `<li>${instance.escapeExpression(item)}</li>`).join("") +
                    "</ul>");
            }
            return "";
        }
        else if (values.length === 1) {
            return values[0];
        }
        return "";
    });
};
exports.registerViewHandlebarsHelpers = registerViewHandlebarsHelpers;
